import React, { useState } from 'react';
import { Alert, Box, Button, Chip, Typography } from '@mui/material';
import { DIdAutocomplete, DModal, DPageLoader } from '../../../../../dum';
import { OrderQuotationIcon } from '../../../../icons';
import {
    clearSalesModule,
    useLinkOrderQuotationProductMutation,
    useOrderQuotationLinkProductQuery,
} from '../../../../../../services';
import { formatFloat } from '../../../../../../helpers';

interface ISLinkOrderQuotationProductDialogProps {
    open: boolean;
    setOpen: (open: boolean) => void;
    orderQuotationId: number | null;
}

// A focused action for PERSISTED free lines: point one at a real product WITHOUT
// the full quotation upsert (which would rewrite the entire lines set). This is
// the "Ventas apunta la línea libre al producto ya creado" step. The backend
// (linkOrderQuotationProduct) sets ONLY product_id, refuses a relink or an
// incompatible group weight, and only while Borrador/Enviada before acceptance
// starts. See the plan, "Allow linking a persisted free line to a product".
export function SLinkOrderQuotationProductDialog({
    open,
    setOpen,
    orderQuotationId,
}: ISLinkOrderQuotationProductDialogProps) {
    // Per-line product selection and per-line error, keyed by the line id.
    const [selection, setSelection] = useState<Record<number, number | null>>(
        {},
    );
    const [errors, setErrors] = useState<Record<number, string>>({});

    const { data, loading, refetch } = useOrderQuotationLinkProductQuery({
        skip: !orderQuotationId || !open,
        variables: { OrderQuotationId: orderQuotationId! },
        fetchPolicy: 'cache-and-network',
    });

    const [linkMutation, { loading: linking }] =
        useLinkOrderQuotationProductMutation({
            update: (cache) => {
                clearSalesModule(cache);
            },
        });

    const quotation = data?.getOrderQuotation;
    const products = data?.getProducts ?? [];
    const lines = quotation?.order_quotation_products ?? [];
    const freeLines = lines.filter((line) => line.product_id == null);

    const handleSetOpen = (nextOpen: boolean) => {
        if (!nextOpen) {
            // The selection is only a dialog draft. Reopening must start empty,
            // and an error raised for the old selection must not come back.
            setSelection({});
            setErrors({});
        }
        setOpen(nextOpen);
    };

    const handleLink = async (lineId: number) => {
        const productId = selection[lineId];
        if (!productId) return;
        setErrors((prev) => ({ ...prev, [lineId]: '' }));
        try {
            await linkMutation({
                variables: {
                    OrderQuotationProductId: lineId,
                    ProductId: productId,
                },
            });
            await refetch();
        } catch (e: any) {
            setErrors((prev) => ({
                ...prev,
                [lineId]:
                    e?.message || 'No se pudo vincular el producto a la línea.',
            }));
        }
    };

    return (
        <DModal
            icon={<OrderQuotationIcon />}
            title={'Vincular producto a línea libre'}
            open={open}
            setOpen={handleSetOpen}
        >
            {loading && !quotation ? (
                <DPageLoader />
            ) : !quotation ? null : (
                <Box
                    sx={{
                        p: 2,
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 2,
                    }}
                >
                    <Typography variant={'subtitle1'}>
                        Cotización #{quotation.order_code}
                    </Typography>

                    {freeLines.length === 0 ? (
                        <Alert severity={'success'}>
                            Esta cotización no tiene líneas libres pendientes de
                            vincular.
                        </Alert>
                    ) : (
                        <>
                            <Typography
                                variant={'body2'}
                                color={'text.secondary'}
                            >
                                Selecciona el producto ya creado por Producción
                                para cada línea libre. El peso por bulto debe
                                coincidir con el del producto; no se modifican
                                cantidades ni precios.
                            </Typography>
                            {freeLines.map((line) => {
                                const selectedProductId = selection[line.id!];
                                const selectedProduct = products.find(
                                    (product) =>
                                        product.id === selectedProductId,
                                );
                                const selectedGroupWeight =
                                    selectedProduct?.current_group_weight || 0;
                                const groupWeightMismatch =
                                    !!selectedProduct &&
                                    Number(line.group_weight) !==
                                        Number(selectedGroupWeight);

                                return (
                                    <Box
                                        key={line.id}
                                        sx={{
                                            display: 'flex',
                                            flexDirection: 'column',
                                            gap: 1,
                                            borderTop: '1px solid',
                                            borderColor: 'divider',
                                            pt: 1.5,
                                        }}
                                    >
                                        <Box
                                            sx={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 1,
                                                flexWrap: 'wrap',
                                            }}
                                        >
                                            <Chip
                                                size={'small'}
                                                color={'warning'}
                                                variant={'outlined'}
                                                label={'Línea libre'}
                                            />
                                            <Typography variant={'body2'}>
                                                {line.proposed_description}
                                            </Typography>
                                            <Typography
                                                variant={'caption'}
                                                color={'text.secondary'}
                                            >
                                                Peso bulto:{' '}
                                                {formatFloat(line.group_weight)}{' '}
                                                · Bultos:{' '}
                                                {formatFloat(line.groups)} ·
                                                Kilos: {formatFloat(line.kilos)}
                                            </Typography>
                                        </Box>
                                        <Box
                                            sx={{
                                                display: 'flex',
                                                alignItems: 'flex-start',
                                                gap: 1,
                                            }}
                                        >
                                            <Box sx={{ flex: 1 }}>
                                                <DIdAutocomplete
                                                    id={
                                                        selectedProductId ??
                                                        null
                                                    }
                                                    onChange={(id) => {
                                                        setSelection(
                                                            (prev) => ({
                                                                ...prev,
                                                                [line.id!]: id,
                                                            }),
                                                        );
                                                        // The prior server error
                                                        // belongs to the prior
                                                        // selection. The live
                                                        // alert below is derived
                                                        // from the new product.
                                                        setErrors((prev) => ({
                                                            ...prev,
                                                            [line.id!]: '',
                                                        }));
                                                    }}
                                                    itemText={
                                                        'compound_description'
                                                    }
                                                    label={'Producto'}
                                                    options={products}
                                                    disableGutters
                                                />
                                            </Box>
                                            <Button
                                                variant={'contained'}
                                                disabled={
                                                    !selectedProductId ||
                                                    groupWeightMismatch ||
                                                    linking
                                                }
                                                onClick={() =>
                                                    handleLink(line.id!)
                                                }
                                            >
                                                Vincular
                                            </Button>
                                        </Box>
                                        {selectedProduct ? (
                                            <Alert
                                                severity={
                                                    groupWeightMismatch
                                                        ? 'error'
                                                        : 'success'
                                                }
                                            >
                                                {groupWeightMismatch
                                                    ? `El peso por bulto de la línea (${formatFloat(
                                                          line.group_weight,
                                                      )}) no coincide con el del producto (${formatFloat(
                                                          selectedGroupWeight,
                                                      )}).`
                                                    : 'El peso por bulto coincide; el producto se puede vincular.'}
                                            </Alert>
                                        ) : null}
                                        {errors[line.id!] ? (
                                            <Alert severity={'error'}>
                                                {errors[line.id!]}
                                            </Alert>
                                        ) : null}
                                    </Box>
                                );
                            })}
                        </>
                    )}
                </Box>
            )}
        </DModal>
    );
}
